import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { StyledCheckIcon } from "@/react/components/styled/StyledIcons";
import InlineBlock from "@/react/features/styled/InlineBlock";
import { COLORS } from "@/react/theme/constants";
import { EMPTY_FUNCTION } from "@/react/empty";

const shrinkAnimation = keyframes`
  from {
    height: calc(100% - 32px);
  }
  to {
    height: 60px;
  }
`;

const FileUploadContainer = styled.div`
  width: calc(100% - 32px);
  height: 100%;
  margin: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 2px dashed ${COLORS.secondary};
  border-radius: 8px;
  color: ${COLORS.link};
  transition: background-color 0.3s, border-color 0.3s, height 0.5s;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: ${COLORS.tertiary};
    border-color: ${COLORS.primary};
  }

  &.dragging-valid {
    background-color: ${COLORS.lightLink};
    border-color: ${COLORS.link};
    color: ${COLORS.link};
  }

  &.dragging-invalid {
    background-color: ${COLORS.lightDanger};
    border-color: ${COLORS.danger};
    color: ${COLORS.danger};
  }

  ${(props) =>
    props.isUploaded &&
    css`
      height: 60px;
      background-color: ${COLORS.lightSuccess};
      border-color: ${COLORS.success};
      color: ${COLORS.success};
      cursor: default;
      animation: ${shrinkAnimation} 1s forwards;
    `}
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileUpload = ({
  onFileUpload,
  isUploaded,
  validMimeTypes,
  infoMessages,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isValidFileType, setIsValidFileType] = useState(true); // New state for file type validation

  const handleDragOver = (e) => {
    e.preventDefault();
    const isValid = validateFileType(e.dataTransfer.items[0]);
    setIsValidFileType(isValid);
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
    setIsValidFileType(true);
  };

  const triggerFileUpload = (files) => {
    if (files.length > 0 && validateFileType(files[0])) {
      setFileName(files[0].name);
      onFileUpload(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    triggerFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    triggerFileUpload(files);
    // Reset the input value to allow re-selection of the same file
    e.target.value = null;
  };

  // Validate file type (only allow .mp3, .mp4, and .wav)
  const validateFileType = (item) => {
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/MIME_types/Common_types
    const fileType = item && item.type ? item.type : "";
    return validMimeTypes.includes(fileType);
  };

  const className =
    isDragging && (isValidFileType ? "dragging-valid" : "dragging-invalid");

  return (
    <FileUploadContainer
      onDragOver={!isUploaded ? handleDragOver : EMPTY_FUNCTION}
      onDragLeave={!isUploaded ? handleDragLeave : EMPTY_FUNCTION}
      onDrop={!isUploaded ? handleDrop : EMPTY_FUNCTION}
      className={className}
      isUploaded={isUploaded}
      onClick={() =>
        !isUploaded && document.getElementById("fileInput").click()
      }
    >
      {isUploaded && fileName ? (
        <InlineBlock marginTop={16}>
          <StyledCheckIcon size={20} color={COLORS.success} />
          <p>{`Uploaded: ${fileName}`}</p>
        </InlineBlock>
      ) : (
        <div>
          {infoMessages.map((message) => (
            <div>{message}</div>
          ))}
        </div>
      )}
      {/**  Hiding the input and triggering it programmatically for
            custom styling and improved UX
      */}
      <HiddenFileInput
        id="fileInput"
        type="file"
        accept={validMimeTypes.join(", ")}
        onChange={handleFileSelect}
        disabled={isUploaded}
      />
    </FileUploadContainer>
  );
};

export default FileUpload;
